import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SvgIcon } from '@progress/kendo-react-common';
import { chevronDownIcon, searchIcon, xIcon } from '@progress/kendo-svg-icons';
import { DlpLookupModel } from 'services/DlpConfiguration/DlpConfigurationService';
import { useClickOutside } from './useClickOutside';
import { normalizeDlpLookupName } from 'utils/dlpLookupNames';

interface LookupMultiSelectProps {
    options: DlpLookupModel[];
    value: string[];
    onChange: (value: string[]) => void;
    placeholder?: string;
    // Options to flag in the dropdown (e.g. brands already used by another scope)
    warnIds?: string[];
    warnTag?: string;
}

// Searchable multi-select over an id/name lookup, working with plain id arrays.
export function LookupMultiSelect({ options, value, onChange, placeholder, warnIds = [], warnTag }: LookupMultiSelectProps) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const ref = useRef<HTMLDivElement>(null);
    useClickOutside(ref, useCallback(() => setOpen(false), []));

    const nameCounts = useMemo(() => {
        const counts = new Map<string, number>();
        options.forEach(option => {
            const key = normalizeDlpLookupName(option.name);
            counts.set(key, (counts.get(key) ?? 0) + 1);
        });
        return counts;
    }, [options]);
    const optionById = useMemo(() => new Map(options.map(option => [option.id, option])), [options]);
    const displayName = (option: DlpLookupModel) => (nameCounts.get(normalizeDlpLookupName(option.name)) ?? 0) > 1
        ? `${option.name} (${option.id})` : option.name;
    const term = search.toLowerCase();
    const filtered = options.filter(o => o.name.toLowerCase().includes(term) || o.id.toLowerCase().includes(term));
    const toggle = (id: string) => onChange(value.includes(id) ? value.filter(v => v !== id) : [...value, id]);

    return (
        <div className="dropdown" ref={ref}>
            <button type="button" className="dropdown-trigger" onClick={() => setOpen(o => !o)}>
                <span>{value.length ? t('dlpConfiguration.selectedCount', { count: value.length }) : placeholder}</span>
                <SvgIcon icon={chevronDownIcon} size="small" />
            </button>
            {value.length > 0 && (
                <div className="chip-row" style={{ marginTop: 6 }}>
                    {value.map(id => (
                        <span key={id} className="tag">
                            {optionById.has(id) ? displayName(optionById.get(id)!) : id}
                            <button type="button" onClick={() => toggle(id)}><SvgIcon icon={xIcon} size="xsmall" /></button>
                        </span>
                    ))}
                </div>
            )}
            {open && (
                <div className="dropdown-panel">
                    <div className="dropdown-search">
                        <SvgIcon icon={searchIcon} size="small" />
                        <input autoFocus placeholder={t('general.search')} value={search} onChange={e => setSearch(e.target.value)} />
                    </div>
                    <div className="dropdown-list">
                        {filtered.map(o => (
                            <label key={o.id} className="dropdown-option checkbox">
                                <input type="checkbox" checked={value.includes(o.id)} onChange={() => toggle(o.id)} />
                                {displayName(o)}
                                {warnIds.includes(o.id) && <span className="already-tag">{warnTag}</span>}
                            </label>
                        ))}
                        {filtered.length === 0 && <div className="dropdown-empty">{t('dlpConfiguration.noMatch', { search })}</div>}
                    </div>
                </div>
            )}
        </div>
    );
}
